/**
 * Minimal i18n for LabelFlow.
 *
 * Deliberately dependency-free and modelled on the theme switcher: the choice
 * is a string in localStorage (`lf-lang`, mirroring `lf-theme`) and components
 * re-render through a hook that listens for a change event.
 *
 * Usage:
 *   const { t, lang, changeLanguage } = useI18n();
 *   <h5>{t('videoToImages.title')}</h5>
 *   {t('videoToImages.jobs.frames', { count: 12 })}
 *
 * Keys are dotted paths into the bundles in ./en.js and ./ko.js. A missing key
 * falls back to English and then to the key itself, so a half-translated
 * bundle degrades instead of rendering "undefined".
 */

import { useCallback, useEffect, useState } from 'react';
import en from './en';
import ko from './ko';

const BUNDLES = { en, ko };
const FALLBACK_LANG = 'en';
const STORAGE_KEY = 'lf-lang';
const CHANGE_EVENT = 'lf-langchange';

export const LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'ko', label: '한국어', short: 'KO' },
];

export const getCurrentLanguage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return BUNDLES[stored] ? stored : FALLBACK_LANG;
  } catch (error) {
    return FALLBACK_LANG;
  }
};

export const setLanguage = (code) => {
  const next = BUNDLES[code] ? code : FALLBACK_LANG;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch (error) {
    // Private-mode browsers can refuse writes; the in-memory switch still works.
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next }));
  return next;
};

const lookup = (bundle, key) =>
  key.split('.').reduce((node, part) => (node == null ? undefined : node[part]), bundle);

/** Resolve a key in `lang`, filling `{placeholders}` from `vars`. */
export const translate = (lang, key, vars) => {
  let value = lookup(BUNDLES[lang] || BUNDLES[FALLBACK_LANG], key);
  if (typeof value !== 'string') {
    value = lookup(BUNDLES[FALLBACK_LANG], key);
  }
  if (typeof value !== 'string') return key;
  if (!vars) return value;
  return value.replace(/\{(\w+)\}/g, (match, name) =>
    vars[name] === undefined || vars[name] === null ? match : String(vars[name])
  );
};

/** Re-renders the calling component whenever the language changes. */
export const useI18n = () => {
  const [lang, setLang] = useState(getCurrentLanguage);

  useEffect(() => {
    const onChange = () => setLang(getCurrentLanguage());
    window.addEventListener(CHANGE_EVENT, onChange);
    // Keep other browser tabs in step.
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);

  const t = useCallback((key, vars) => translate(lang, key, vars), [lang]);

  return { lang, t, changeLanguage: setLanguage };
};
